import { apiClient, handleApiError, isBackendEnabled } from '../client';
import {
    ApiResponse,
    StoreLayoutResponse,
    StoresResponse,
    StoreResponse,
    GetStoresParams,
    Store,
    StoresArray,
} from '../types';
import { mockStoreApi } from '../mock';

/**
 * Store API Service
 * Handles store layout and store information API calls
 * Uses backend API if enabled, otherwise uses mock/local data
 */
export const storeApi = {
    /**
     * Get store layout with nodes and product locations
     */
    getStoreLayout: async (storeId: string): Promise<StoreLayoutResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockStoreApi.getStoreLayout(storeId);
        }

        try {
            const response = await apiClient.get<ApiResponse<StoreLayoutResponse>>(
                `/stores/${storeId}/layout`
            );
            return response.data.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get all stores with optional filtering
     */
    getStores: async (params?: GetStoresParams): Promise<StoresResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockStoreApi.getStores();
        }

        try {
            // Backend returns array directly, not wrapped in ApiResponse
            const response = await apiClient.get<StoresArray>('/store', {
                params,
            });
            const stores = response.data;
            // Transform array response to StoresResponse format
            return {
                stores,
                total: stores.length,
            };
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get a single store by ID
     */
    getStoreById: async (storeId: string | number): Promise<StoreResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockStoreApi.getStoreById(storeId.toString());
        }

        try {
            // Try to get single store endpoint first, fallback to filtering from all stores
            try {
                const response = await apiClient.get<Store>(`/store/${storeId}`);
                return { store: response.data };
            } catch {
                // Fallback: get all stores and filter by id
                const response = await apiClient.get<StoresArray>('/store');
                const stores = response.data;
                const storeIdNum = typeof storeId === 'string' ? parseInt(storeId, 10) : storeId;
                const store = stores.find(s => s.id === storeIdNum);
                if (!store) {
                    throw new Error(`Store with ID ${storeId} not found`);
                }
                return { store };
            }
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get nearby stores based on coordinates
     */
    getNearbyStores: async (
        latitude: number,
        longitude: number,
        radius: number = 10
    ): Promise<StoresResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockStoreApi.getNearbyStores();
        }

        try {
            const response = await apiClient.get<ApiResponse<StoresResponse>>('/stores/nearby', {
                params: {
                    latitude,
                    longitude,
                    radius,
                },
            });
            return response.data.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get stores in a specific city
     */
    getStoresByCity: async (city: string): Promise<StoresResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockStoreApi.getStoresByCity();
        }

        try {
            const response = await apiClient.get<ApiResponse<StoresResponse>>('/stores/city', {
                params: {
                    city,
                },
            });
            return response.data.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },
};

